import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatDate } from '@/lib/utils';
import { 
    CheckCircle, 
    XCircle, 
    Clock, 
    Eye,
    Package,
    User,
    Tag,
    Loader2,
    AlertTriangle,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Link } from 'react-router-dom';

const AdminModerationTab = () => {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    
    // Reject dialog
    const [rejectDialog, setRejectDialog] = useState({ open: false, listing: null });
    const [rejectReason, setRejectReason] = useState('');
    
    // Preview dialog
    const [previewDialog, setPreviewDialog] = useState({ open: false, listing: null });

    const fetchPendingListings = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/moderation/pending');
            setListings(res.data.listings || []);
        } catch (error) {
            console.error('Error fetching pending listings:', error);
            toast.error('Eroare la încărcarea listărilor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingListings();
    }, []);

    const handleApprove = async (listing) => {
        setActionLoading(listing.listing_id);
        try {
            await api.post(`/admin/moderation/${listing.listing_id}/approve`);
            toast.success(`Listarea "${listing.title}" a fost aprobată`);
            setListings(prev => prev.filter(l => l.listing_id !== listing.listing_id));
        } catch (error) {
            console.error('Error approving listing:', error);
            toast.error('Eroare la aprobarea listării');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!rejectDialog.listing) return;
        
        setActionLoading(rejectDialog.listing.listing_id);
        try {
            await api.post(`/admin/moderation/${rejectDialog.listing.listing_id}/reject`, {
                reason: rejectReason || 'Nu respectă regulile platformei'
            });
            toast.success(`Listarea "${rejectDialog.listing.title}" a fost respinsă`);
            setListings(prev => prev.filter(l => l.listing_id !== rejectDialog.listing.listing_id));
            setRejectDialog({ open: false, listing: null });
            setRejectReason('');
        } catch (error) {
            console.error('Error rejecting listing:', error);
            toast.error('Eroare la respingerea listării');
        } finally {
            setActionLoading(null);
        }
    };

    const getListingTypeLabel = (type) => {
        return type === 'auction' ? 'Licitație' : 'Preț fix';
    };

    const getListingPrice = (listing) => {
        if (listing.listing_type === 'auction') {
            return listing.starting_price || listing.current_price;
        }
        return listing.buy_now_price || listing.starting_price;
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Moderare conținut</h2>
                </div>
                <div className="grid gap-4">
                    {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-32 w-full" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                        <Clock className="w-5 h-5 text-orange-500" />
                        Listări în așteptare
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {listings.length} {listings.length === 1 ? 'listare așteaptă' : 'listări așteaptă'} aprobarea
                    </p>
                </div>
                <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchPendingListings}
                    className="gap-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Reîncarcă
                </Button>
            </div>

            {listings.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">Nicio listare în așteptare</h3>
                        <p className="text-muted-foreground">
                            Toate listările au fost procesate. Revino mai târziu.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {listings.map((listing) => (
                        <Card key={listing.listing_id} className="overflow-hidden" data-testid={`pending-listing-${listing.listing_id}`}>
                            <CardContent className="p-4">
                                <div className="flex gap-4">
                                    {/* Image - clickable to open preview */}
                                    <div 
                                        className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden bg-gray-100 shrink-0 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                                        onClick={() => setPreviewDialog({ open: true, listing })}
                                    >
                                        {listing.images?.[0] ? (
                                            <img 
                                                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/thumbnail/${listing.images[0]}?w=200`}
                                                alt={listing.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Package className="w-8 h-8 text-gray-400" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <h3 
                                                className="font-semibold truncate cursor-pointer hover:text-primary hover:underline"
                                                onClick={() => setPreviewDialog({ open: true, listing })}
                                            >
                                                {listing.title}
                                            </h3>
                                            <Badge variant="outline" className="shrink-0">
                                                {getListingTypeLabel(listing.listing_type)}
                                            </Badge>
                                        </div>
                                        
                                        <div className="space-y-1 text-sm text-muted-foreground mb-3">
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4" />
                                                <span>{listing.seller_username || listing.seller_name}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Tag className="w-4 h-4" />
                                                <span>{listing.category_name} {listing.subcategory_name ? `/ ${listing.subcategory_name}` : ''}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-4 h-4" />
                                                <span>{formatDate(listing.created_at)}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-primary text-lg">
                                                {getListingPrice(listing)?.toLocaleString('ro-RO')} RON
                                            </span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-col gap-2 shrink-0">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setPreviewDialog({ open: true, listing })}
                                            className="gap-1"
                                        >
                                            <Eye className="w-4 h-4" />
                                            <span className="hidden md:inline">Previzualizare</span>
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="gap-1 bg-green-600 hover:bg-green-700"
                                            onClick={() => handleApprove(listing)}
                                            disabled={actionLoading === listing.listing_id}
                                            data-testid={`approve-btn-${listing.listing_id}`}
                                        >
                                            {actionLoading === listing.listing_id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4" />
                                            )}
                                            <span className="hidden md:inline">Aprobă</span>
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            className="gap-1"
                                            onClick={() => setRejectDialog({ open: true, listing })}
                                            disabled={actionLoading === listing.listing_id}
                                            data-testid={`reject-btn-${listing.listing_id}`}
                                        >
                                            <XCircle className="w-4 h-4" />
                                            <span className="hidden md:inline">Respinge</span>
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Reject Dialog */}
            <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, listing: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-500" />
                            Respinge listarea
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Ești sigur că vrei să respingi listarea <strong>"{rejectDialog.listing?.title}"</strong>?
                        </p>
                        <div className="space-y-2">
                            <Label>Motiv respingere (opțional)</Label>
                            <Textarea
                                placeholder="Ex: Imaginile nu sunt conforme, descrierea lipsește detalii, etc."
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialog({ open: false, listing: null })}>
                            Anulează
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={handleReject}
                            disabled={actionLoading === rejectDialog.listing?.listing_id}
                        >
                            {actionLoading === rejectDialog.listing?.listing_id ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : null}
                            Respinge
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewDialog.open} onOpenChange={(open) => !open && setPreviewDialog({ open: false, listing: null })}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Previzualizare listare</DialogTitle>
                    </DialogHeader>
                    {previewDialog.listing && (
                        <div className="space-y-4">
                            {/* Images Section */}
                            <div>
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">Imagini</h4>
                                {previewDialog.listing.images?.length > 0 ? (
                                    <div className="grid grid-cols-3 gap-3">
                                        {previewDialog.listing.images.map((img, idx) => (
                                            <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100 border">
                                                <img 
                                                    src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/thumbnail/${img}?w=400`}
                                                    alt={`Imagine ${idx + 1}`}
                                                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                                                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${img}`, '_blank')}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-gray-100 rounded-lg p-6 text-center">
                                        <Package className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-muted-foreground">Nicio imagine încărcată</p>
                                    </div>
                                )}
                            </div>

                            {/* Details */}
                            <div className="space-y-3">
                                <div>
                                    <h3 className="font-semibold text-lg">{previewDialog.listing.title}</h3>
                                    <p className="text-primary font-bold text-xl">
                                        {getListingPrice(previewDialog.listing)?.toLocaleString('ro-RO')} RON
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Vânzător:</span>
                                        <p className="font-medium">{previewDialog.listing.seller_username}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Tip:</span>
                                        <p className="font-medium">{getListingTypeLabel(previewDialog.listing.listing_type)}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Categorie:</span>
                                        <p className="font-medium">
                                            {previewDialog.listing.category_name}
                                            {previewDialog.listing.subcategory_name && ` / ${previewDialog.listing.subcategory_name}`}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Condiție:</span>
                                        <p className="font-medium capitalize">{previewDialog.listing.condition}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Livrare:</span>
                                        <p className="font-medium">
                                            {previewDialog.listing.shipping_cost > 0 
                                                ? `${previewDialog.listing.shipping_cost} RON` 
                                                : 'Gratuită'}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Acceptă schimburi:</span>
                                        <p className="font-medium">{previewDialog.listing.accepts_trades ? 'Da' : 'Nu'}</p>
                                    </div>
                                </div>

                                <div>
                                    <span className="text-muted-foreground text-sm">Descriere:</span>
                                    <p className="mt-1 whitespace-pre-wrap text-sm">{previewDialog.listing.description}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewDialog({ open: false, listing: null })}>
                            Închide
                        </Button>
                        <Button 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                                handleApprove(previewDialog.listing);
                                setPreviewDialog({ open: false, listing: null });
                            }}
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aprobă
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={() => {
                                setRejectDialog({ open: true, listing: previewDialog.listing });
                                setPreviewDialog({ open: false, listing: null });
                            }}
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Respinge
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminModerationTab;
